import type { StatusTone } from "./status";
import type { AppLogLevel } from "./types";

/**
 * The server's log, read the way pino-pretty used to print it on a terminal: a
 * coloured level, the message, and the record's fields inline — with the whole
 * record one click away.
 */

/** The level filter, as presets rather than a multi-select: nobody wants "debug and fatal". */
export const LEVEL_PRESETS = ["all", "info", "warn", "error", "unknown"] as const;
export type LevelPreset = (typeof LEVEL_PRESETS)[number];

export const LEVEL_PRESET_LABELS: Readonly<Record<LevelPreset, string>> = {
  all: "Everything",
  info: "Info and above",
  warn: "Warnings and above",
  error: "Errors only",
  unknown: "Unstructured only",
};

const ORDER: readonly AppLogLevel[] = ["info", "warn", "error", "fatal"];

/**
 * The `level` parameter for a preset. Every preset above `all` keeps `unknown`:
 * those are the lines pino did not write — a crash's stack trace, mostly — and a
 * filter for "errors" that hid them would hide the worst error there is.
 */
export function levelsFor(preset: LevelPreset): string | undefined {
  if (preset === "all") return undefined;
  if (preset === "unknown") return "unknown";
  return [...ORDER.slice(ORDER.indexOf(preset)), "unknown"].join(",");
}

export const WINDOWS = ["15m", "1h", "24h", "7d", "all"] as const;
export type LogWindow = (typeof WINDOWS)[number];

export const WINDOW_LABELS: Readonly<Record<LogWindow, string>> = {
  "15m": "Last 15 minutes",
  "1h": "Last hour",
  "24h": "Last 24 hours",
  "7d": "Last 7 days",
  all: "Everything kept",
};

const WINDOW_MS: Readonly<Record<Exclude<LogWindow, "all">, number>> = {
  "15m": 15 * 60_000,
  "1h": 60 * 60_000,
  "24h": 24 * 60 * 60_000,
  "7d": 7 * 24 * 60 * 60_000,
};

/** Computed per request, so a live view's window moves with the clock. */
export function windowStart(window: LogWindow, now: Date): string | undefined {
  if (window === "all") return undefined;
  return new Date(now.getTime() - WINDOW_MS[window]).toISOString();
}

const TONES: Readonly<Record<AppLogLevel, StatusTone>> = {
  trace: "idle",
  debug: "charging",
  info: "available",
  warn: "transitional",
  error: "faulted",
  fatal: "faulted",
  unknown: "idle",
};

export function levelTone(level: AppLogLevel): StatusTone {
  return TONES[level];
}

const MAX_INLINE = 80;

/**
 * The record's fields as pino-pretty would have trailed them. `cpId` is left out
 * because it has its own column; an `err` is shown by its message, which is what
 * anybody reads first.
 */
export function inlineFields(data: Readonly<Record<string, unknown>>): [string, string][] {
  return Object.entries(data)
    .filter(([key]) => key !== "cpId")
    .map(([key, value]) => [key, shorten(inlineValue(key, value))]);
}

function inlineValue(key: string, value: unknown): string {
  if (key === "err" && typeof value === "object" && value !== null && "message" in value) {
    return String(value.message);
  }
  return typeof value === "string" ? value : JSON.stringify(value);
}

function shorten(text: string): string {
  return text.length > MAX_INLINE ? `${text.slice(0, MAX_INLINE)}…` : text;
}

export type JsonTokenKind = "key" | "string" | "number" | "literal" | "punctuation";

export interface JsonToken {
  readonly kind: JsonTokenKind;
  readonly text: string;
}

/** A string, optionally followed by the colon that makes it a key; or a number, or a literal. */
const TOKEN =
  /("(?:\\.|[^"\\])*")(\s*:)?|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|\b(?:true|false|null)\b/g;

/**
 * Pretty JSON split into coloured runs, rendered as spans rather than injected
 * HTML: log content is whatever a station or a stranger sent, and it never gets
 * near `innerHTML`.
 */
export function jsonTokens(value: unknown): JsonToken[] {
  const text = JSON.stringify(value, null, 2) ?? "undefined";
  const tokens: JsonToken[] = [];
  let last = 0;

  for (const match of text.matchAll(TOKEN)) {
    const start = match.index;
    if (start > last) tokens.push({ kind: "punctuation", text: text.slice(last, start) });

    const [whole, quoted, colon] = match;
    if (quoted !== undefined && colon !== undefined) {
      tokens.push({ kind: "key", text: quoted }, { kind: "punctuation", text: colon });
    } else if (quoted !== undefined) {
      tokens.push({ kind: "string", text: quoted });
    } else {
      tokens.push({ kind: /^[-\d]/.test(whole) ? "number" : "literal", text: whole });
    }
    last = start + whole.length;
  }
  if (last < text.length) tokens.push({ kind: "punctuation", text: text.slice(last) });

  return tokens;
}

/** `HH:MM:SS.mmm` in the viewer's zone: the precision a burst of lines needs. */
export function clockTime(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number, size = 2) => String(value).padStart(size, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 3)}`;
}

export function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function isSameDay(a: string, b: string): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

export interface LiveStateInput {
  readonly pageCount: number;
  /** The previous filter's pages, shown while the new filter's first page loads. */
  readonly isPlaceholder: boolean;
  readonly isPaused: boolean;
}

/**
 * Whether the view is polling. Older pages loaded stop it (a refetch of the top
 * would open a gap below), but only this filter's own — placeholder pages are a
 * different query's, and must not freeze the new one before it has loaded.
 */
export function liveState(input: LiveStateInput): { isReadingBack: boolean; isLive: boolean } {
  const isReadingBack = !input.isPlaceholder && input.pageCount > 1;
  return { isReadingBack, isLive: !input.isPaused && !isReadingBack };
}
